import React, { useState, useEffect, useRef } from 'react';

/**
 * Who is doing this - reviewing a film, or joining a viewing session.
 *
 * This replaced a dropdown of predefined names, which was a Quick Join
 * leftover: back then people had no account and picked themselves off a list.
 * Since the passwordless overhaul every member signs in and carries a profile
 * name, so in the normal case there is nothing to ask - it shows who they are
 * and gets out of the way.
 *
 * The override covers the two cases a profile name does not: several people
 * watching on one login, and anyone who has not set a name.
 */

const ACCENTS = {
  orange: {
    label: 'text-orange-200',
    value: 'text-orange-100',
    border: 'border-orange-500',
    link: 'text-orange-300 hover:text-orange-100'
  },
  purple: {
    label: 'text-purple-200',
    value: 'text-purple-100',
    border: 'border-purple-500',
    link: 'text-purple-300 hover:text-purple-100'
  }
};

const NameField = ({
  name,
  onNameChange,
  profileName = '',
  action = 'Posting',
  accent = 'orange',
  onEnter
}) => {
  const [overriding, setOverriding] = useState(false);
  const styles = ACCENTS[accent] || ACCENTS.orange;

  // Parents rebuild their setters on every render, so this is reached through
  // a ref. Depending on the prop identity would re-run the effect constantly
  // and overwrite whatever the person was in the middle of typing.
  const changeRef = useRef(onNameChange);
  changeRef.current = onNameChange;

  // Hand the profile name up as the answer unless it is being overridden. Also
  // covers the name arriving late, when the profile loads after the modal.
  useEffect(() => {
    if (profileName && !overriding) changeRef.current(profileName);
  }, [profileName, overriding]);

  if (profileName && !overriding) {
    return (
      <div>
        <p className={`text-sm ${styles.label}`}>
          {action} as <span className={`font-bold ${styles.value}`}>{profileName}</span>
        </p>
        <button
          type="button"
          onClick={() => {
            setOverriding(true);
            changeRef.current('');
          }}
          className={`mt-2 text-sm underline ${styles.link}`}
        >
          Use a different name
        </button>
      </div>
    );
  }

  return (
    <div>
      <label className={`block text-sm mb-2 ${styles.label}`}>Enter your name:</label>
      <input
        type="text"
        placeholder="Your name"
        value={name || ''}
        onChange={(e) => onNameChange(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === 'Enter' && onEnter) onEnter();
        }}
        className={`w-full bg-gray-700 border ${styles.border} text-white rounded px-3 py-2`}
      />
      {profileName && (
        <button
          type="button"
          onClick={() => setOverriding(false)}
          className={`mt-2 text-sm underline ${styles.link}`}
        >
          Back to {profileName}
        </button>
      )}
    </div>
  );
};

export default NameField;
